import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardActions, Stack, Typography, List, ListItem, IconButton, Button, Box, useTheme, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { Task } from './types';
import { useTranslation } from 'react-i18next';

export type TaskFilterType = 'store' | 'user';

interface TasksCardProps {
  tasks: Task[];
  taskFilter?: TaskFilterType[];
  onTaskFilterChange?: (filter: TaskFilterType[]) => void;
}

export function TasksCard({ tasks, taskFilter = ['store', 'user'], onTaskFilterChange }: TasksCardProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [internalFilter, setInternalFilter] = useState<TaskFilterType[]>(taskFilter);

  const activeFilter = onTaskFilterChange ? taskFilter : internalFilter;
  const handleFilterChange = (_: React.MouseEvent<HTMLElement>, newFilter: TaskFilterType[]) => {
    // Ensure at least one is selected
    if (newFilter.length === 0) return;
    if (onTaskFilterChange) {
      onTaskFilterChange(newFilter);
    } else {
      setInternalFilter(newFilter);
    }
  };

  // Filter tasks based on active filter
  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      // If task has no taskType, treat it as 'store' by default
      const taskType = task.taskType || 'store';
      return activeFilter.includes(taskType);
    });
  }, [tasks, activeFilter]);

  const pageSize = 3;
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(filteredTasks.length / pageSize));
  const pageItems = filteredTasks.slice(page * pageSize, page * pageSize + pageSize);

  const formatSecondary = useMemo(() => (t: Task) => {
    const parts: string[] = [];
    if (t.location) parts.push(t.location);
    if (t.dueDisplay) parts.push(t.dueDisplay);
    return parts.join(' | ');
  }, []);

  return (
    <Card sx={{ borderRadius: 3, minHeight: '350px', backgroundColor: theme.palette.custom.gray[100], boxShadow: '0 1px 2px rgba(16,24,40,0.04),0 0 0 1px rgba(16,24,40,0.08)' }}>
      <CardContent sx={{ pb: 0, pt: '18px', px: '20px' }}>
        <Stack direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: '4px' }}>
          <Typography variant="textLG"
            fontWeight={700}
            sx={{ fontSize: 16 }}>Today&apos;s Tasks</Typography>
          <ToggleButtonGroup
            size="small"
            value={activeFilter}
            onChange={handleFilterChange}
            sx={{
              '& .MuiToggleButton-root': {
                px: '12px',
                py: '2px',
                fontSize: 12,
                textTransform: 'none',
                border: `1px solid ${theme.palette.custom.gray[300]}`,
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                },
              },
            }}
          >
            <ToggleButton value="store">Store</ToggleButton>
            <ToggleButton value="user">User</ToggleButton>
          </ToggleButtonGroup>
        </Stack >
        {/* List */}
        {
          filteredTasks.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography
                variant="textSM"
                color="text.secondary"
              >
                No tasks to display
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {pageItems.map((t, idx) => (
                <ListItem
                  key={t.id}
                  disableGutters
                  divider={idx < pageItems.length - 1}
                  sx={{
                    alignItems: 'center',
                    px: 0,
                    py: '10px',
                    margin: '6px 0',
                    '&:last-of-type': { pb: '10px' },
                  }}>
                  <Box sx={{
                    mr: '6px',
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    bgcolor: t.isOverdue ? 'rgba(239, 68, 68, 0.15)' : theme.palette.common.white,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    svg: { fontSize: 20, color: t.isOverdue ? '#DC2626' : '#1E293B' },
                    position: 'relative',
                  }}>
                    {t.icon}
                  </Box>
                  <Box sx={{ flexGrow: 1, pr: 1 }}>
                    <Stack direction="row"
                      alignItems="center"
                      spacing={1}>
                      <Typography variant="textSM"
                        component="div"
                        sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3, color: t.isOverdue ? '#DC2626' : '#111928', mb: '2px' }}>
                        {t.title}
                      </Typography>

                    </Stack>
                    <Typography variant="textXS"
                      component="div"
                      sx={{ fontSize: 12.5, color: '#5A6475' }}>
                      {formatSecondary(t)}
                    </Typography>
                  </Box>
                  <Box sx={{ ml: 1 }}>
                    <Button
                      onClick={() => t.href && navigate(t.href)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: 13,
                        color: '#111928',
                        minWidth: 0,
                        px: '2px',
                        padding: 0,
                        border: 'none',
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          textDecoration: 'underline',
                          boxShadow: 'none',
                        },
                        '&:focus': {
                          backgroundColor: 'transparent',
                          boxShadow: 'none',
                        },
                      }}>
                      Review
                    </Button>
                  </Box>
                </ListItem>
              ))}
            </List>
          )
        }
      </CardContent >
      <CardActions sx={{ px: '10px', pt: 1, pb: '10px', margin: '4px 8px', display: 'flex', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <IconButton
            aria-label={t('backPage')}
            size="small"
            disabled={page === 0}
            onClick={() => setPage(p => Math.max(0, p - 1))}
            sx={{ width: 36, height: 36, border: '1px solid #E2E8F0', borderRadius: 1, mr: 1 }}>
            <ArrowBack fontSize="small" />
          </IconButton>
          <Typography variant="textXS"
            sx={{ flexGrow: 1, textAlign: 'center', fontSize: 12.5, color: '#374151', fontWeight: 500 }}>
            Page {page + 1} of {pageCount}
          </Typography>
          <IconButton
            aria-label={t('forwardPage')}
            size="small"
            disabled={page >= pageCount - 1}
            onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
            sx={{ width: 36, height: 36, border: '1px solid #E2E8F0', borderRadius: 1, ml: 1 }}>
            <ArrowForward fontSize="small" />
          </IconButton>
        </Box>
      </CardActions>
    </Card >
  );
}
